import type { IRRData } from '../../models/IRRDataPoint';
import type { IRRUpdate } from '../../models/IRRUpdate';

/** CSV-quotes a value when it contains a comma, quote, or newline. */
export function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function csvRow(cells: string[]): string {
  return cells.map(csvCell).join(',');
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Builds a blank starting-point CSV matching the columns and summary rows a
 * real cashflow report upload expects (Date/Discrete Value/Cumulative Value,
 * then IRR/ROE/MM), covering the next 12 months from today so an admin has a
 * ready row per period to fill in. A trailing "Instructions" column — read by
 * no parser, just left in place the way the real sample data's own "Note"
 * column was — carries first-time-use guidance so the file explains itself.
 */
export function buildCashflowReportTemplateCsv(): string {
  const rows = [csvRow(['Date', 'Discrete Value', 'Cumulative Value', 'Instructions'])];
  const start = new Date();
  start.setDate(1);
  const monthDate = (offset: number) => {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };

  rows.push(
    csvRow([
      monthDate(0),
      '',
      '',
      'One row per month. Date format: YYYY-MM-01 (the first day of the month). ' +
        'Discrete Value = that month’s net cashflow (negative = money paid out, positive = money returned). ' +
        'Cumulative Value = running total of every Discrete Value up to and including this month.',
    ]),
  );
  for (let i = 1; i < 12; i += 1) {
    rows.push(csvRow([monthDate(i), '', '', '']));
  }
  rows.push(
    csvRow([
      monthDate(12),
      '',
      '',
      'Need more or fewer months? Add or delete rows above — just keep one row per month in the same format.',
    ]),
  );

  rows.push(csvRow(['', '', '', '']));
  rows.push(
    csvRow(['IRR', '', '', 'Internal Rate of Return, as a decimal (e.g. 0.2429 means 24.29%).']),
  );
  rows.push(csvRow(['ROE', '', '', 'Return on Equity, as a decimal (e.g. 0.3444 means 34.44%).']));
  rows.push(csvRow(['MM', '', '', 'Money Multiple, written with an "x" suffix (e.g. 1.34x).']));
  return rows.join('\n');
}

/**
 * Exports an already-uploaded report's parsed data in the same column
 * layout as the upload format (and the blank template above), so a
 * downloaded report can be edited and re-uploaded directly.
 */
export function buildCashflowReportCsv(update: IRRUpdate, data?: IRRData): string {
  const rows = [csvRow(['Date', 'Discrete Value', 'Cumulative Value'])];
  for (const point of data?.historical ?? []) {
    rows.push(
      csvRow([
        `${point.month}-01`,
        point.discrete_value != null ? String(point.discrete_value) : '',
        point.cumulative_value != null ? String(point.cumulative_value) : '',
      ]),
    );
  }
  rows.push('');
  rows.push(csvRow(['IRR', String(update.irr)]));
  rows.push(csvRow(['ROE', String(update.roe)]));
  rows.push(csvRow(['MM', `${update.mm}x`]));
  return rows.join('\n');
}
